import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from './utils/api';
import './AuditLog.css';

const ACTION_COLORS = {
  login: '#4caf50', logout: '#607d8b',
  'user.created': '#2196f3', 'user.updated': '#00bcd4',
  'user.deactivated': '#f44336', 'user.reactivated': '#8bc34a',
  'password.changed': '#ff9800', 'password.reset': '#e91e63',
  'permission.updated': '#9c27b0',
};

export default function AuditLog() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [stats, setStats] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [error, setError] = useState('');

  const loadLog = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, per_page: 50 });
      if (actionFilter) params.append('action', actionFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const res = await apiFetch(`/api/v1/admin/audit?${params}`);
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
        setTotal(data.total || 0);
      }
    } catch { setError('Failed to load audit log'); }
  }, [page, actionFilter, dateFrom, dateTo]);

  const loadStats = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/admin/audit/stats');
      if (res.ok) setStats(await res.json());
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadLog(); loadStats(); }, [loadLog, loadStats]);

  const actionTypes = stats?.by_action ? Object.keys(stats.by_action) : [];
  const totalPages = Math.ceil(total / 50);

  return (
    <div className="audit-log">
      <h1>Audit Log</h1>
      {error && <div className="al-error">{error}</div>}

      {stats && (
        <div className="al-stats">
          <div className="stat-card">
            <span className="stat-value">{total}</span>
            <span className="stat-label">Total Events</span>
          </div>
          {Object.entries(stats.by_action || {}).slice(0, 5).map(([action, count]) => (
            <div key={action} className="stat-card">
              <span className="stat-value">{count}</span>
              <span className="stat-label">{action}</span>
            </div>
          ))}
        </div>
      )}

      <div className="al-toolbar">
        <select value={actionFilter} onChange={e => { setActionFilter(e.target.value); setPage(1); }}>
          <option value="">All Actions</option>
          {actionTypes.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="From" />
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="To" />
      </div>

      <table className="al-table">
        <thead>
          <tr><th>Timestamp</th><th>User</th><th>Action</th><th>Target</th><th>IP</th></tr>
        </thead>
        <tbody>
          {items.map(item => (
            <React.Fragment key={item.id}>
              <tr onClick={() => setExpanded(expanded === item.id ? null : item.id)} className="clickable">
                <td>{item.created_at ? new Date(item.created_at).toLocaleString() : '—'}</td>
                <td>
                  <div className="user-info">
                    <span className="user-name">{item.user_name}</span>
                    <span className="user-email">{item.user_email}</span>
                  </div>
                </td>
                <td>
                  <span className="action-badge" style={{ background: ACTION_COLORS[item.action] || '#666' }}>
                    {item.action}
                  </span>
                </td>
                <td>{item.target_type ? `${item.target_type}` : '—'}</td>
                <td>{item.ip_address || '—'}</td>
              </tr>
              {expanded === item.id && (
                <tr className="detail-row">
                  <td colSpan={5}>
                    <div className="detail-content">
                      <div><strong>ID:</strong> {item.id}</div>
                      <div><strong>Target ID:</strong> {item.target_id || '—'}</div>
                      <div><strong>Details:</strong>
                        <pre>{JSON.stringify(item.details, null, 2)}</pre>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
          {items.length === 0 && <tr><td colSpan={5} className="empty-cell">No audit entries found</td></tr>}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div className="al-pagination">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>←</button>
          <span>{page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>→</button>
        </div>
      )}
    </div>
  );
}
